import React, { useMemo } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { Popover, PopoverProps } from '../Popover/Popover';
import { Container } from './Tooltip.styles';
import styled from 'styled-components';
import { useDelayedHover } from '@mantine/core';

type TooltipProps = Omit<PopoverProps, 'body'> & {
  reverseEllipsis?: boolean; // New prop to control ellipsis direction
  description: string | React.ReactNode;
  className?: string;
  openDelay?: number;
};

// An extension to the LongContentPopover component for adding a description to the popover
export default function Tooltip({
  description,
  children,
  position = 'top',
  reverseEllipsis = false, // Default value is false
  className,
  openDelay = 1000,
  ...rest
}: TooltipProps) {
  const [opened, { open, close }] = useDisclosure();

  const { openDropdown, closeDropdown } = useDelayedHover({
    open,
    close,
    openDelay: openDelay,
    closeDelay: 0,
  });

  const tooltipContent = <StyledTooltip>{description}</StyledTooltip>;

  const normalizedChildren = useMemo(() => {
    if (typeof children !== 'string') {
      return children;
    }

    // if reverseEllipsis is true, remove '/' chars, '../', and './' from the beginning of the string
    if (reverseEllipsis) {
      return children.replace(/^(\/+|\.\.\/|\.\/)+/g, '');
    }

    return children;
  }, [children]);

  return (
    <Popover
      body={tooltipContent}
      opened={opened}
      position={position}
      {...rest}
      clickable={false}
    >
      <Container
        className={className}
        onMouseEnter={openDropdown}
        onMouseLeave={closeDropdown}
        $reverseEllipsis={reverseEllipsis} // Pass the prop to the styled component
      >
        {normalizedChildren}
      </Container>
    </Popover>
  );
}

const StyledTooltip = styled.span`
  display: flex;
  flex-direction: row;
  gap: 4px;
  max-width: 300px;
  white-space: pre-wrap;
  word-wrap: break-word;
`;
